import { CSSProperties, MouseEvent, useId, useState } from 'react';
// MUI on purpose: BUI 0.15 MenuListBox traps Tab, locks scroll and has no checkbox rows.
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { sameProjects } from '../../modules/shared/projectSelection';

export type ProjectPickerOption = {
  name: string;
  tag?: string;
  tagTitle?: string;
};

const NARROW = '@media (max-width: 760px)';

// Shared head cut after its last hyphen, none when it would empty a name.
export const sharedPrefix = (names: string[]): string => {
  if (names.length < 2) return '';
  const head = names.reduce((common, name) => {
    let shared = common;
    while (!name.startsWith(shared)) shared = shared.slice(0, -1);
    return shared;
  });
  const cut = head.lastIndexOf('-');
  const prefix = cut > 2 ? head.slice(0, cut + 1) : '';
  return names.includes(prefix) ? '' : prefix;
};

const tailOf = (name: string, prefix: string) =>
  prefix && name.startsWith(prefix) ? name.slice(prefix.length) : name;

const summaryOf = (names: string[], prefix: string, allLabel: string) => {
  if (names.length === 0) return allLabel;
  if (names.length === 1) return names[0];
  const tails = names
    .slice(0, 2)
    .map(name => tailOf(name, prefix))
    .join(', ');
  return names.length > 2 ? `${tails} +${names.length - 2}` : tails;
};

// Known names in option order; every option ticked reads as All projects.
const draftOf = (names: string[], known: string[]) => {
  const kept = known.filter(name => names.includes(name));
  return kept.length === known.length ? [] : kept;
};

const toggle = (draft: string[], name: string) => {
  if (draft.length === 0) return [name];
  return draft.includes(name)
    ? draft.filter(other => other !== name)
    : [...draft, name];
};

const wrapperSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: 0,
  [NARROW]: { flexWrap: 'wrap', width: '100%' },
};

const triggerSx = (open: boolean): SxProps<Theme> => ({
  justifyContent: 'space-between',
  gap: '12px',
  minWidth: 230,
  maxWidth: 440,
  height: 36,
  padding: '0 10px 0 12px',
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  textAlign: 'left',
  background: 'var(--bui-bg-neutral-1)',
  border: '1px solid',
  borderColor: open ? 'var(--bui-bg-solid)' : 'var(--bui-border-2)',
  borderRadius: 'var(--bui-radius-3)',
  boxShadow: open ? '0 0 0 3px var(--bui-bg-info)' : 'none',
  '&:hover': {
    borderColor: open ? 'var(--bui-bg-solid)' : 'var(--bui-fg-secondary)',
  },
  '&.Mui-focusVisible': {
    outline: '2px solid var(--bui-bg-solid)',
    outlineOffset: '2px',
  },
  '&.Mui-disabled': { color: 'var(--bui-fg-disabled)' },
  [NARROW]: { minWidth: 0, width: '100%', maxWidth: 'none' },
});

const paperSx: SxProps<Theme> = {
  mt: '8px',
  width: 480,
  maxHeight: 'min(470px, calc(100% - 96px))',
  border: '1px solid var(--bui-border-2)',
  borderRadius: 'var(--bui-radius-3)',
  backgroundImage: 'none',
};

const itemSx: SxProps<Theme> = {
  gap: '10px',
  minHeight: 36,
  px: '10px',
  py: 0,
  borderRadius: 'var(--bui-radius-2)',
  fontSize: 13,
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: 'var(--bui-bg-neutral-2)',
  },
};

const checkboxSx: SxProps<Theme> = {
  p: 0,
  pointerEvents: 'none',
  color: 'var(--bui-fg-secondary)',
  '&.Mui-checked': { color: 'var(--bui-bg-solid)' },
};

const labelStyle: CSSProperties = {
  fontFamily: 'var(--bui-font-monospace)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: 'var(--bui-fg-secondary)',
  whiteSpace: 'nowrap',
};

const valueStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const namesStyle: CSSProperties = {
  fontFamily: 'var(--bui-font-monospace)',
  fontSize: 12.5,
  fontWeight: 500,
};

const optionNameStyle: CSSProperties = {
  ...valueStyle,
  flex: 1,
  fontFamily: 'var(--bui-font-monospace)',
  fontSize: 12.5,
};

const mutedStyle: CSSProperties = { color: 'var(--bui-fg-secondary)' };

const tagStyle: CSSProperties = {
  flex: 'none',
  fontFamily: 'var(--bui-font-monospace)',
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: 'var(--bui-fg-secondary)',
  background: 'var(--bui-bg-neutral-2)',
  border: '1px solid var(--bui-border-1)',
  borderRadius: 999,
  padding: '2px 8px',
};

const separatorStyle: CSSProperties = {
  height: 1,
  margin: '6px 4px',
  background: 'var(--bui-border-1)',
};

const Name = ({ name, prefix }: { name: string; prefix: string }) => {
  const tail = tailOf(name, prefix);
  return (
    <>
      {tail !== name && <span style={mutedStyle}>{prefix}</span>}
      {tail}
    </>
  );
};

const Tick = ({ checked }: { checked: boolean }) => (
  <Checkbox
    checked={checked}
    size="small"
    tabIndex={-1}
    disableRipple
    aria-hidden
    sx={checkboxSx}
  />
);

const Chevron = ({ up, animate }: { up: boolean; animate: boolean }) => (
  <svg
    aria-hidden
    width="12"
    height="12"
    viewBox="0 0 12 12"
    style={{
      flex: 'none',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.8,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      color: 'var(--bui-fg-secondary)',
      transform: up ? 'rotate(180deg)' : undefined,
      transition: animate ? 'transform .15s ease' : undefined,
    }}
  >
    <path d="M2.5 4.5L6 8l3.5-3.5" />
  </svg>
);

export const ProjectPicker = ({
  options,
  selected,
  onApply,
  label = 'Project',
  allLabel = 'All projects',
  disabled = false,
}: {
  options: ProjectPickerOption[];
  selected: string[];
  onApply: (next: string[]) => void;
  label?: string;
  allLabel?: string;
  disabled?: boolean;
}) => {
  const labelId = useId();
  const menuId = useId();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const open = anchorEl !== null;
  const known = options.map(option => option.name);
  const prefix = sharedPrefix(known);
  const shown = open ? draft : selected;
  const summary = summaryOf(shown, prefix, allLabel);

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    setDraft(draftOf(selected, known));
    setAnchorEl(event.currentTarget);
  };

  // Escape, Tab and a click outside all close through here, and all apply.
  const close = () => {
    setAnchorEl(null);
    const next = draftOf(draft, known);
    if (!sameProjects(next, selected)) onApply(next);
  };

  return (
    <Box sx={wrapperSx}>
      <span id={labelId} style={labelStyle}>
        {label}
      </span>
      <ButtonBase
        disableRipple
        disabled={disabled}
        onClick={openMenu}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${label}: ${summary}`}
        title={shown.length > 1 ? shown.join(', ') : undefined}
        sx={triggerSx(open)}
      >
        <span style={valueStyle}>
          {shown.length === 0 ? (
            allLabel
          ) : (
            <span style={namesStyle}>
              {shown.length === 1 ? (
                <Name name={shown[0]} prefix={prefix} />
              ) : (
                summary
              )}
            </span>
          )}
        </span>
        <Chevron up={open} animate={!reducedMotion} />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        variant="menu"
        disableScrollLock
        transitionDuration={reducedMotion ? 0 : 120}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: paperSx } }}
        MenuListProps={{
          id: menuId,
          'aria-labelledby': labelId,
          sx: { p: '6px' },
        }}
      >
        <MenuItem
          dense
          disableRipple
          role="menuitemcheckbox"
          aria-checked={draft.length === 0}
          onClick={() => setDraft([])}
          sx={itemSx}
        >
          <Tick checked={draft.length === 0} />
          <span style={{ fontWeight: 600 }}>{allLabel}</span>
        </MenuItem>
        <li role="separator" style={separatorStyle} />
        {options.map(option => {
          const ticked = draft.includes(option.name);
          return (
            <MenuItem
              key={option.name}
              dense
              disableRipple
              role="menuitemcheckbox"
              aria-checked={ticked}
              onClick={() =>
                setDraft(current =>
                  draftOf(toggle(current, option.name), known),
                )
              }
              sx={itemSx}
            >
              <Tick checked={ticked} />
              <span title={option.name} style={optionNameStyle}>
                <Name name={option.name} prefix={prefix} />
              </span>
              {option.tag && (
                <span title={option.tagTitle} style={tagStyle}>
                  {option.tag}
                </span>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
